import { CircularProgress, IconButton } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import type { FC } from 'react';
import React, { useRef, useState } from 'react';

import { useMarketplaceContract } from '../../../../hooks/marketplace-contract.hooks';
import {
  getIsEthListing,
  getIsStarsListing,
  getListingStarsPrice,
} from '../../../../lib/graph/graph-utils';
import type { Listing } from '../../../../lib/graph/MogulMarketplace';
import { AssetType } from '../../../../lib/graph/MogulMarketplace';
import {
  ethToWei,
  getNetworkCurrencySymbol,
  weiToEth,
} from '../../../../lib/smart-contract-utils';
import { formatFloatField, parseFloatField } from '../../../../utils/form.util';
import { Button } from '../../../core/button';
import { InputField } from '../../../core/input-field';
import { Panel } from '../../panel/Panel.component';
import styles from './ModifyNFTListingPriceModal.module.css';
import { WalletETHBalanceCard } from './WalletETHBalanceCard.component';

export interface ModifyNFTListingPriceModalProps {
  listing: Listing;
}
export interface ModifyNFTListingPrice extends ModifyNFTListingPriceModalProps {
  hideModal: () => void;
}

export interface ModifyNFTListingPriceFormValues {
  newStarsListingPrice?: number;
  newEthListingPrice?: number;
  toAddress?: string;
}

const ModifyNftListingPrice: FC<ModifyNFTListingPrice> = ({
  listing,
  hideModal,
}) => {
  const marketplaceContract = useMarketplaceContract({
    type: listing.asset.type ?? AssetType.Erc1155,
  });

  const [newPriceAmount, setNewPriceAmount] = useState<string>(
    weiToEth(getListingStarsPrice(listing) as string),
  );
  const [submitting, setSubmitting] = useState(false);
  const [isStarsPrice, setIsStarsPrice] = useState(getIsStarsListing(listing));

  const selection =
    useRef<{
      start: number | null;
      end: number | null;
      element: HTMLInputElement;
    } | null>(null);

  const input = <T extends keyof ModifyNFTListingPriceFormValues>(
    field: T,
    parse: (value: string) => string | undefined,
    format: (value: number) => string,
  ) => ({
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.currentTarget;

      const value = target.value;
      const parsed = parse(value);

      selection.current = {
        element: target,
        start: target.selectionStart,
        end: target.selectionEnd,
      };

      if (parsed != null) {
        setNewPriceAmount(parsed);
      }
    },
    value: format(parseFloat(newPriceAmount)),
  });

  const handleClose = () => {
    if (submitting) {
      return;
    }

    hideModal();
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();
    if (submitting || !marketplaceContract.initialized) {
      return;
    }

    setSubmitting(true);

    try {
      await marketplaceContract.changeListingPrice(
        listing.listingId as string,
        ethToWei(newPriceAmount),
        isStarsPrice,
      );
    } catch (error) {
      setSubmitting(false);
      throw error;
    }

    setSubmitting(false);

    handleClose();
  };

  return (
    <Panel
      className="overflow-hidden"
      bodyClassName={styles.panel + ' flex flex-col rounded-lg m-8'}
    >
      <div className="flex flex-row">
        <div
          className={styles.modifyNFTListingPriceInfo + ' flex flex-col m-8'}
        >
          <div className="flex">
            <div className={'flex flex-col pl-6 pt-6'}>
              <div className={styles.title + ' text-center py-4'}>
                Modify Listing Price
              </div>

              <form autoComplete="off" onSubmit={handleSubmit} className="mb-6">
                <p className={styles.inputLabel}>New Listing Price</p>
                <select
                  name="Token Type"
                  id="tokenType"
                  className={styles.selectPriceType}
                  onBlur={event => {
                    setIsStarsPrice(event.target.value === 'stars');
                  }}
                >
                  {getIsStarsListing(listing) && (
                    <option value="stars">MOGUL</option>
                  )}
                  {getIsEthListing(listing) && (
                    <option value="eth">{getNetworkCurrencySymbol()}</option>
                  )}
                </select>
                <InputField
                  label=""
                  hideLabel
                  InputProps={{
                    disabled: !marketplaceContract.initialized || submitting,
                    className: styles.inputField,
                    ...input(
                      isStarsPrice
                        ? 'newStarsListingPrice'
                        : 'newEthListingPrice',
                      parseFloatField,
                      formatFloatField,
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  disabled={!marketplaceContract.initialized || submitting}
                >
                  {!submitting ? (
                    'Change'
                  ) : (
                    <>
                      Changing
                      <CircularProgress
                        className={'placeholder-white::placeholder ml-4'}
                        size={20}
                      />
                    </>
                  )}
                </Button>
              </form>
              <div className="mt-auto mb-4">
                <WalletETHBalanceCard
                  walletBalance={
                    marketplaceContract.walletDetails.balance.native.formatted
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className="ml-4 relative">
          <IconButton
            className={'h-10 w-10 top-2 right-2 z-30'}
            style={{
              background: '#000000',
              border: 'none',
              position: 'absolute',
            }}
            onClick={hideModal}
          >
            <ClearIcon style={{ color: 'white' }} />
          </IconButton>
        </div>
      </div>
    </Panel>
  );
};
export const ModifyNFTListingPriceModal =
  (props: ModifyNFTListingPriceModalProps) =>
  ({ hideModal }: ModifyNFTListingPrice) => {
    return <ModifyNftListingPrice hideModal={hideModal} {...props} />;
  };
